import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export interface ILinks {
  eStatement: string;
  eSafetyBox: string;
  eSignature: string;
  createNewTemplate: string;
  filledArchiveTemplate: string;
  filledArchiveMonthlyTemplate: string;
  activeTemplate: string;
  updateTemplate: string;
  otherTemplate: string;
  archiveTemplate: string;
}

const path: Record<string, string> = {
  eStatement: 'oldPages/e-notification/e-statement/index.html',
  eSignature: 'oldPages/e-notification/e-signature/index.html',
  eSafetyBox: 'oldPages/e-notification/e-safety-box/index.html',
};

function useENotification() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPath, setCurrentPath] = useState<string>();

  const links: ILinks = useMemo(
    () => ({
      eStatement: '/infoTrac/e-notification?tab=eStatement',
      eSafetyBox: '/infoTrac/e-notification?tab=eSafetyBox',
      eSignature: '/infoTrac/e-notification?tab=eSignature',
      createNewTemplate: `/infoTrac/e-notification/create-new-template?tab=${searchParams.get('tab') || 'eStatement'}`,
      filledArchiveTemplate: `/infoTrac/e-notification/archive-template/filled-template?tab=${searchParams.get('tab') || 'eStatement'}`,
      // eslint-disable-next-line max-len
      filledArchiveMonthlyTemplate: `/infoTrac/e-notification/archive-template/filled-monthly-template?tab=${searchParams.get('tab') || 'eStatement'}`,
      activeTemplate: `/infoTrac/e-notification/active-template?tab=${searchParams.get('tab') || 'eStatement'}`,
      updateTemplate: `/infoTrac/e-notification/filled-template?tab=${searchParams.get('tab') || 'eStatement'}`,
      otherTemplate: `/infoTrac/e-notification/other-template?tab=${searchParams.get('tab') || 'eStatement'}`,
      archiveTemplate: `/infoTrac/e-notification/archive-template?tab=${searchParams.get('tab') || 'eStatement'}`,
    }),
    [searchParams],
  );

  useEffect(() => {
    const handleIframeLoad = () => {
      const iframeDocument = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;

      if (!iframeDocument) return;

      const nav_estat = iframeDocument.getElementById('nav_estat');
      const nav_safety = iframeDocument.getElementById('nav_safety');
      const nav_esign = iframeDocument.getElementById('nav_esign');

      const createNewTemplate = iframeDocument.getElementById('create-new-template');
      const activeTemplate = iframeDocument.getElementById('active-template');
      const otherTemplate = iframeDocument.getElementById('other-template');
      const archiveTemplate = iframeDocument.getElementById('archive-template');
      const updateTemplate = iframeDocument.getElementsByClassName('updateTemplate');
      const updateMonthlyTemplate = iframeDocument.getElementsByClassName('updateMonthlyTemplate');
      const cancelTemplate = iframeDocument.getElementsByClassName('cancelTemplate');

      if (cancelTemplate) {
        Array.from(cancelTemplate).forEach(el =>
          el.addEventListener('click', e => {
            e.preventDefault();
            navigate(links[(searchParams.get('tab') || 'eStatement') as keyof ILinks]);
          }),
        );
      }

      if (updateMonthlyTemplate) {
        Array.from(updateMonthlyTemplate).forEach(el =>
          el.addEventListener('click', e => {
            e.preventDefault();
            navigate(links.filledArchiveMonthlyTemplate);
          }),
        );
      }

      if (updateTemplate) {
        Array.from(updateTemplate).forEach(el =>
          el.addEventListener('click', e => {
            e.preventDefault();
            const link = (e.target as HTMLElement).closest('a');
            const href = link?.getAttribute('href') || '';
            const idMatch = href.match(/[?&]id=([^&]+)/);
            const templateId = idMatch ? decodeURIComponent(idMatch[1]) : null;
            const baseLink = pathname.includes('/archive-template')
              ? links.filledArchiveTemplate
              : links.updateTemplate;

            navigate(templateId ? `${baseLink}&id=${encodeURIComponent(templateId)}` : baseLink);
          }),
        );
      }

      if (otherTemplate) {
        otherTemplate.addEventListener('click', e => {
          e.preventDefault();
          navigate(links.otherTemplate);
        });
      }

      if (archiveTemplate) {
        archiveTemplate.addEventListener('click', e => {
          e.preventDefault();
          navigate(links.archiveTemplate);
        });
      }

      if (createNewTemplate) {
        createNewTemplate.addEventListener('click', e => {
          e.preventDefault();
          navigate(links.createNewTemplate);
        });
      }

      if (activeTemplate) {
        activeTemplate.addEventListener('click', e => {
          e.preventDefault();
          navigate(links.activeTemplate);
        });
      }

      if (nav_estat) {
        if (searchParams.get('tab') === 'eStatement') {
          nav_estat.classList.add('active');
        }
        nav_estat.addEventListener('click', e => {
          e.preventDefault();
          navigate(links.eStatement);
        });
      }
      if (nav_safety) {
        if (searchParams.get('tab') === 'eSafetyBox') {
          nav_safety.classList.add('active');
        }
        nav_safety.addEventListener('click', e => {
          e.preventDefault();
          navigate(links.eSafetyBox);
        });
      }
      if (nav_esign) {
        if (searchParams.get('tab') === 'eSignature') {
          nav_esign.classList.add('active');
        }
        nav_esign.addEventListener('click', e => {
          e.preventDefault();
          navigate(links.eSignature);
        });
      }
    };

    if (iframeRef.current) iframeRef.current.addEventListener('load', handleIframeLoad);

    return () => {
      if (iframeRef.current) iframeRef.current.removeEventListener('load', handleIframeLoad);
    };
  }, [navigate, currentPath]);

  useEffect(() => {
    if (!searchParams.has('tab')) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);

        next.set('tab', 'eStatement');

        return next;
      });
    }
    setCurrentPath(path[searchParams.get('tab') || 'eStatement']);
  }, [searchParams, setSearchParams]);

  return { iframeRef, currentPath };
}

export default useENotification;
